import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useLocation } from 'wouter';
import { useTranslation } from '@/i18n/LanguageContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Coins, Trophy, Lock, Calendar, Award } from 'lucide-react';
import Header from '@/components/header';
import { format } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';

// Define interfaces for type safety
interface User {
  id: number;
  uid: string;
  email: string;
  displayName?: string;
  avatarUrl?: string;
  cacatais: number;
  createdAt: string;
  youtube_member: boolean;
}

interface SolvedByUser {
  id: number;
  displayName?: string;
  avatarUrl?: string;
}

interface CustomPuzzle {
  id: number;
  title: string;
  description: string;
  address: string;
  prize: number;
  userId: number;
  user?: {
    id: number;
    displayName?: string;
    avatarUrl?: string;
  };
  imageUrl?: string;
  solved: boolean;
  solvedByUserId?: number;
  solvedByUser?: SolvedByUser;
  solvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export default function UserProfilePage() {
  const { displayName } = useParams();
  const [, setLocation] = useLocation();
  const { t, formatNumber } = useTranslation();

  // Fetch user information by display name
  const { data: user, isLoading: userLoading, error: userError } = useQuery<User>({
    queryKey: [`/api/users/by-name/${displayName}`],
    enabled: !!displayName,
  });

  // Fetch custom puzzles created by the user
  const { data: userPuzzles, isLoading: puzzlesLoading } = useQuery<CustomPuzzle[]>({
    queryKey: [`/api/puzzles/custom/user-by-name/${displayName}`],
    enabled: !!displayName,
  });

  // Fetch custom puzzles solved by the user
  const { data: solvedPuzzles, isLoading: solvedPuzzlesLoading } = useQuery<CustomPuzzle[]>({
    queryKey: [`/api/puzzles/custom/solved-by-name/${displayName}`],
    enabled: !!displayName,
  });

  // Handle loading and error states
  if (userLoading) {
    return (
      <>
        <Header />
        <UserProfileSkeleton t={t} />
      </>
    );
  }

  if (userError || !user) {
    return (
      <div className="min-h-screen p-8">
        <Header />
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <h2 className="text-xl font-semibold mb-2">{t('user_profile.user_not_found')}</h2>
                <p className="text-gray-500 mb-4">
                  {t('user_profile.user_not_found_description')}
                </p>
                <Button onClick={() => setLocation('/')}>
                  {t('user_profile.back_to_home')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900">
      <Header />
      <div className="container mx-auto pt-24 px-4 md:px-6 pb-16 max-w-4xl space-y-8">
        {/* User Profile Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <Avatar className="h-24 w-24 md:h-32 md:w-32">
                <AvatarImage src={user.avatarUrl || ''} />
                <AvatarFallback className="text-2xl">
                  {user.displayName?.charAt(0) || user.email.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2 justify-center md:justify-start">
                  {user.displayName || 'Anonymous User'}
                  {user.youtube_member && (
                    <Badge variant="outline" className="ml-2 border-yellow-500 text-yellow-500">
                      <Award className="h-3 w-3 mr-1 inline" /> {t('user_profile.youtube_member')}
                    </Badge>
                  )}
                </h1>
                <div className="flex items-center justify-center md:justify-start mt-2 text-gray-500">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>Member since {format(new Date(user.createdAt), 'MMMM yyyy')}</span>
                </div>
                <div className="flex items-center gap-2 text-xl mt-1">
                  <Coins className="h-5 w-5 text-yellow-500" />
                  <span className="font-medium">{formatNumber(user.cacatais, 2)} caçatais</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-3 justify-center md:justify-start">
                  {solvedPuzzles && (
                    <div className="bg-gray-800 px-3 py-1 rounded-full text-sm">
                      <span className="font-semibold">{solvedPuzzles.length}</span> {t('user_profile.puzzles_solved')}
                    </div>
                  )}
                  {userPuzzles && (
                    <div className="bg-gray-800 px-3 py-1 rounded-full text-sm">
                      <span className="font-semibold">{userPuzzles.length}</span> {t('user_profile.puzzles_created')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for User Content */}
        <Tabs defaultValue="created" className="space-y-4">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="created">
              <Trophy className="h-4 w-4 mr-2" />
              {t('user_profile.puzzles_created')}
            </TabsTrigger>
            <TabsTrigger value="solved">
              <Award className="h-4 w-4 mr-2" />
              {t('user_profile.puzzles_solved')}
            </TabsTrigger>
          </TabsList>

          {/* Created Puzzles Tab */}
          <TabsContent value="created" className="space-y-4">
            <Card className="border border-gray-800 bg-gradient-to-b from-gray-900 to-gray-950 shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  {t('user_profile.puzzles_created')} 
                  {user.displayName ? (
                    <a
                      href={`/users/${user.displayName?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || user.id}`}
                      className="hover:underline hover:text-yellow-400 transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        setLocation(`/users/${user.displayName?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || user.id}`);
                      }}
                    >
                      {user.displayName}
                    </a>
                  ) : (
                    'User'
                  )}
                </CardTitle>
                <CardDescription>
                  {t('user_profile.puzzles_created_description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {puzzlesLoading ? (
                  <div className="grid grid-cols-1 gap-6">
                    {Array(3).fill(0).map((_, i) => (
                      <div key={i} className="border rounded-lg overflow-hidden">
                        <div className="flex flex-col md:flex-row">
                          <Skeleton className="w-full md:w-40 h-40" />
                          <div className="p-4 flex-1 space-y-2">
                            <Skeleton className="h-6 w-3/4" />
                            <Skeleton className="h-4 w-1/4" />
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-4 w-1/3" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : userPuzzles && userPuzzles.length > 0 ? (
                  <div className="grid grid-cols-1 gap-6">
                    {userPuzzles.map((puzzle) => (
                      <div key={puzzle.id} className="group border border-gray-800 hover:border-yellow-600/50 rounded-xl overflow-hidden shadow-md hover:shadow-xl hover:shadow-yellow-900/10 transition-all duration-300 bg-gradient-to-br from-gray-900 to-gray-950">
                        <div className="flex flex-col md:flex-row">
                          <div className="w-full md:w-48 h-48 md:h-full relative overflow-hidden">
                            {puzzle.imageUrl ? (
                              <img 
                                src={puzzle.imageUrl} 
                                alt={puzzle.title} 
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                                <Lock className="h-14 w-14 text-gray-700 opacity-70" />
                              </div>
                            )}
                            {puzzle.solved && (
                              <div className="absolute top-3 right-3">
                                <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg border-0 px-3 py-1">
                                  <Trophy className="w-3 h-3 mr-1" /> Solved
                                </Badge>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>
                          <div className="p-5 flex-1 flex flex-col">
                            <div>
                              <h3 className="text-xl font-bold mb-1 group-hover:text-yellow-500 transition-colors duration-300">{puzzle.title}</h3>
                              <p className="text-sm text-gray-400 mb-3 flex items-center">
                                <Calendar className="h-3 w-3 mr-1 opacity-70" />
                                {format(new Date(puzzle.createdAt), "MMM d, yyyy")}
                              </p>
                              <p className="text-sm line-clamp-2 mb-4 text-gray-300">
                                {puzzle.description}
                              </p>
                            </div>
                            <div className="mt-auto">
                              <div className="inline-flex items-center px-3 py-1.5 bg-yellow-900/30 border border-yellow-800/50 rounded-full mb-3">
                                <Coins className="h-4 w-4 text-yellow-500 mr-2" />
                                <span className="font-medium text-yellow-500">{formatNumber(puzzle.prize, 2)} caçatais</span>
                              </div>
                              {puzzle.solvedByUserId && (
                                <div className="flex items-center mt-2 bg-gray-800/50 p-2 rounded-lg">
                                  <span className="text-xs text-gray-400 mr-2">{t('desafios_cacatais.solved_by', { name: '' })}</span>
                                  <Avatar className="h-6 w-6 mr-2 ring-1 ring-yellow-600/30">
                                    <AvatarImage src={puzzle.solvedByUser?.avatarUrl || ""} />
                                    <AvatarFallback className="bg-yellow-900/50 text-yellow-500">
                                      {puzzle.solvedByUser?.displayName?.charAt(0) || "U"}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm font-medium text-gray-300">
                                    {puzzle.solvedByUser?.displayName ? (
                                      <a
                                        href={`/users/${puzzle.solvedByUser?.displayName?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || puzzle.solvedByUserId}`}
                                        className="hover:underline hover:text-yellow-400 transition-colors"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setLocation(`/users/${puzzle.solvedByUser?.displayName?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || puzzle.solvedByUserId}`);
                                        }}
                                      >
                                        {puzzle.solvedByUser?.displayName}
                                      </a>
                                    ) : 'Anonymous'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <CardFooter className="bg-gray-900/80 backdrop-blur-sm p-4 flex justify-end border-t border-gray-800">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="bg-yellow-900/50 hover:bg-yellow-800 text-yellow-100 border border-yellow-800/50 hover:border-yellow-700"
                            onClick={() => setLocation(`/puzzles/custom/${puzzle.id}`)}
                          >
                            {t('user_profile.view_puzzle')}
                          </Button>
                        </CardFooter>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p>{t('user_profile.no_puzzles_created')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Solved Puzzles Tab */}
          <TabsContent value="solved" className="space-y-4">
            <Card className="border border-gray-800 bg-gradient-to-b from-gray-900 to-gray-950 shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  {t('user_profile.puzzles_solved')} 
                  {user.displayName ? (
                    <a
                      href={`/users/${user.displayName?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || user.id}`}
                      className="hover:underline hover:text-yellow-400 transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        setLocation(`/users/${user.displayName?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || user.id}`);
                      }}
                    >
                      {user.displayName}
                    </a>
                  ) : (
                    'User'
                  )}
                </CardTitle>
                <CardDescription>
                  {t('user_profile.puzzles_solved_description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {solvedPuzzlesLoading ? (
                  <div className="grid grid-cols-1 gap-6">
                    {Array(3).fill(0).map((_, i) => (
                      <div key={i} className="border rounded-lg overflow-hidden">
                        <div className="flex flex-col md:flex-row">
                          <Skeleton className="w-full md:w-40 h-40" />
                          <div className="p-4 flex-1 space-y-2">
                            <Skeleton className="h-6 w-3/4" />
                            <Skeleton className="h-4 w-1/4" />
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-4 w-1/3" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : solvedPuzzles && solvedPuzzles.length > 0 ? (
                  <div className="grid grid-cols-1 gap-6">
                    {solvedPuzzles.map((puzzle) => (
                      <div key={puzzle.id} className="group border border-gray-800 hover:border-green-600/50 rounded-xl overflow-hidden shadow-md hover:shadow-xl hover:shadow-green-900/10 transition-all duration-300 bg-gradient-to-br from-gray-900 to-gray-950">
                        <div className="flex flex-col md:flex-row">
                          <div className="w-full md:w-48 h-48 md:h-full relative overflow-hidden">
                            {puzzle.imageUrl ? (
                              <img 
                                src={puzzle.imageUrl} 
                                alt={puzzle.title} 
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                                <Lock className="h-14 w-14 text-gray-700 opacity-70" />
                              </div>
                            )}
                            <div className="absolute top-3 right-3">
                              <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg border-0 px-3 py-1">
                                <Award className="w-3 h-3 mr-1" /> Solved
                              </Badge>
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>
                          <div className="p-5 flex-1 flex flex-col">
                            <div>
                              <h3 className="text-xl font-bold mb-1 group-hover:text-green-500 transition-colors duration-300">{puzzle.title}</h3>
                              <p className="text-sm text-gray-400 mb-3 flex items-center">
                                <span className="flex items-center mr-2">
                                  <Avatar className="h-4 w-4 mr-1 ring-1 ring-gray-700">
                                    <AvatarImage src={puzzle.user?.avatarUrl || ""} />
                                    <AvatarFallback className="bg-gray-800 text-xs">
                                      {puzzle.user?.displayName?.charAt(0) || "U"}
                                    </AvatarFallback>
                                  </Avatar>
                                  {puzzle.user?.displayName ? (
                                    <a
                                      href={`/users/${puzzle.user?.displayName?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || puzzle.userId}`}
                                      className="hover:underline hover:text-green-400 transition-colors"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setLocation(`/users/${puzzle.user?.displayName?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || puzzle.userId}`);
                                      }}
                                    >
                                      {puzzle.user?.displayName}
                                    </a>
                                  ) : 'Unknown User'}
                                </span>
                              </p>
                              <p className="text-sm line-clamp-2 mb-4 text-gray-300">
                                {puzzle.description}
                              </p>
                            </div>
                            <div className="mt-auto">
                              <div className="inline-flex items-center px-3 py-1.5 bg-green-900/30 border border-green-800/50 rounded-full mb-3">
                                <Coins className="h-4 w-4 text-green-500 mr-2" />
                                <span className="font-medium text-green-500">{formatNumber(puzzle.prize, 2)} {t('common.cacatais')}</span>
                              </div>
                              {puzzle.solvedAt && (
                                <div className="flex items-center mt-2 bg-gray-800/50 p-2 rounded-lg">
                                  <Calendar className="h-4 w-4 text-green-500 mr-2" />
                                  <span className="text-xs text-gray-400 mr-1">{t('user_profile.solved_on')}</span>
                                  <span className="text-sm font-medium text-gray-300">{format(new Date(puzzle.solvedAt), "MMM d, yyyy")}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <CardFooter className="bg-gray-900/80 backdrop-blur-sm p-4 flex justify-end border-t border-gray-800">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="bg-green-900/50 hover:bg-green-800 text-green-100 border border-green-800/50 hover:border-green-700"
                            onClick={() => setLocation(`/puzzles/custom/${puzzle.id}`)}
                          >
                            {t('user_profile.view_puzzle')}
                          </Button>
                        </CardFooter>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p>{t('user_profile.no_puzzles_solved')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Loading skeleton component
function UserProfileSkeleton({ t }: { t: (key: string, options?: any) => string }) {
  return (
    <div className="container mx-auto pt-24 px-4 md:px-6 pb-16 max-w-4xl">
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <Skeleton className="h-24 w-24 md:h-32 md:w-32 rounded-full" />
              <div className="flex-1 space-y-2 w-full text-center md:text-left">
                <Skeleton className="h-8 w-1/2 mx-auto md:mx-0" />
                <Skeleton className="h-4 w-1/3 mx-auto md:mx-0" />
                <Skeleton className="h-4 w-1/4 mx-auto md:mx-0" />
                <div className="mt-4 flex gap-2 justify-center md:justify-start">
                  <Skeleton className="h-6 w-24 rounded-full" />
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Tabs defaultValue="created" className="space-y-4">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="created">{t('user_profile.puzzles_created')}</TabsTrigger>
            <TabsTrigger value="solved">{t('user_profile.puzzles_solved')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="created" className="space-y-4">
            <Card className="border border-gray-800 bg-gradient-to-b from-gray-900 to-gray-950 shadow-xl">
              <CardHeader>
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Array(3).fill(0).map((_, i) => (
                    <div key={i} className="border rounded-lg p-4">
                      <div className="flex flex-col md:flex-row gap-4">
                        <Skeleton className="w-full md:w-40 h-40" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-6 w-3/4" />
                          <Skeleton className="h-4 w-1/4" />
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-4 w-1/3" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
